// FaceLock Scan training captions: one plain English description per kept
// frame, built from what the scan measured (its bucket, the expression, the
// light it passed). NO trigger word: the server adds it. The captions travel
// with the create request as `captions`, aligned with `photos` (see
// scanCreateExtras), and the server hands them to the trainer.
//
//   "head and shoulders, turned to the person's left, neutral expression, even light"
//   "full body, standing, seen from behind"

import type { ScanBucketId } from "@/shared/face-qc/index.mjs";
import type { ScanPhotoLabel } from "@/lib/facelock";
import type { ScanFrame } from "./session";

/** Who the avatar shows, when the page knows; otherwise neutral wording. */
export type CaptionSubject = "man" | "woman" | "person";

const possessive = (s: CaptionSubject) => (s === "man" ? "his" : s === "woman" ? "her" : "the person's");

const FRAMING: Record<string, string> = { face: "head and shoulders", expr: "head and shoulders", upper: "upper body, waist up", full: "full body, standing" };

function pose(bucket: ScanBucketId, s: CaptionSubject): string {
  const their = possessive(s);
  switch (bucket) {
    case "face_front": return "facing the camera";
    case "face_left": return `head turned to ${their} left`;
    case "face_right": return `head turned to ${their} right`;
    case "face_profile_left": return `side profile, head turned to ${their} left`;
    case "face_profile_right": return `side profile, head turned to ${their} right`;
    case "face_chin_up": return "facing the camera, chin raised";
    case "face_chin_down": return "facing the camera, chin lowered";
    case "expr_neutral":
    case "expr_smile":
    case "expr_talk": return "facing the camera";
    case "upper_front": return "facing the camera";
    case "upper_left": return `body turned to ${their} left`;
    case "upper_right": return `body turned to ${their} right`;
    case "full_front": return "facing the camera";
    case "full_left": return `side view, turned to ${their} left`;
    case "full_right": return `side view, turned to ${their} right`;
    case "full_back": return "seen from behind";
    default: return "";
  }
}

function expression(frame: Pick<ScanFrame, "bucket" | "smile" | "jaw">): string {
  if (frame.bucket === "expr_smile") return "smiling";
  if (frame.bucket === "expr_talk") return "talking, mouth open";
  if (frame.bucket === "full_back" || frame.bucket === "full_left" || frame.bucket === "full_right" || frame.bucket === "face_profile_left" || frame.bucket === "face_profile_right") return "";
  const smile = typeof frame.smile === "number" ? frame.smile : null;
  const jaw = typeof frame.jaw === "number" ? frame.jaw : null;
  if (smile !== null && smile >= 0.5) return "smiling";
  if (jaw !== null && jaw >= 0.25) return "mouth open";
  return smile !== null || jaw !== null || frame.bucket.startsWith("face_") || frame.bucket === "expr_neutral" ? "neutral expression" : "";
}

export function captionFor(frame: Pick<ScanFrame, "bucket" | "smile" | "jaw" | "check">, subject: CaptionSubject = "person"): string {
  const group = frame.bucket.split("_")[0];
  const parts = [FRAMING[group] ?? "", pose(frame.bucket, subject), expression(frame)];
  // Every live-checked or measured frame passed the light rules (no backlight, no side shadow).
  if (frame.check !== "basic") parts.push("even light");
  return parts.filter(Boolean).join(", ");
}

// The captions, the labels and the scan marker for the create request, keyed
// by the very File objects the dialog uploads (so its upload order is the
// caption order).
const extras = new WeakMap<File, string>();
const labels = new WeakMap<File, ScanPhotoLabel>();

export function rememberCaption(file: File, caption: string, label?: ScanPhotoLabel): void {
  extras.set(file, caption);
  if (label) labels.set(file, label);
}

/**
 * `{ source: "scan", captions }` when every file came from FaceLock Scan, else nothing; plus `labels` (one per
 * file, same order) when every one of them has its label.
 */
export function scanCreateExtras(files: readonly File[]): { source?: "scan"; captions?: string[]; labels?: ScanPhotoLabel[] } {
  if (!files.length || !files.every((f) => extras.has(f))) return {};
  const scan = { source: "scan" as const, captions: files.map((f) => extras.get(f) as string) };
  return files.every((f) => labels.has(f)) ? { ...scan, labels: files.map((f) => labels.get(f) as ScanPhotoLabel) } : scan;
}
